import { Component, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subject, merge, throwError } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { PhysicalSettlementMatrix } from 'src/app/form-config/mockups/consts/physical-settlement-matrix.const';
import { VALUATION_OBLIGATION_CATEGORIES } from 'src/app/product-capture/model/consts/valuation-obligation-categories';
import { VALUATION_OBLIGATION_CHARACTERISTICS } from 'src/app/product-capture/model/consts/valuation-obligation-characteristics';
import { Currency } from 'src/app/product-capture/model/interfaces/currency.interface';
import { OBLIGATION_CATEGORY_ITEMS, OBLIGATION_CHARACTERISTICS_ITEMS } from 'src/app/static-data/models/consts/reference-entity';
import { ReferenceEntity } from 'src/app/static-data/models/interfaces/static-data';
import { StaticDataService } from 'src/app/static-data/services/static-data.service';

@Component({
  selector: 'app-settlement-method',
  templateUrl: './settlement-method.component.html',
  styleUrls: ['./settlement-method.component.scss']
})
export class SettlementMethodComponent {

  @Input()
  form: UntypedFormGroup;

  @Input()
  labels: Record<string, any>;

  @Input()
  currencies: Currency[];

  @Input()
  entity: ReferenceEntity;

  @Input()
  index: number;

  destroy$ = new Subject();

  obligationCategoryItems: Record<string, any> = OBLIGATION_CATEGORY_ITEMS;

  obligationCharacteristicsItems: Record<string, any> = OBLIGATION_CHARACTERISTICS_ITEMS;

  valuationObligationCategoryItems: Record<string, any> = VALUATION_OBLIGATION_CATEGORIES;

  valuationObligationCharacteristicsItems: Record<string, any> = VALUATION_OBLIGATION_CHARACTERISTICS;

  constructor(private staticDataService: StaticDataService) {
      
  }

  ngOnInit(): void {
    this.addRuleToCurrencyField('domesticCurrency', 'NOT_DOMESTIC_CURRENCY');
    this.addRuleToCurrencyField('specifiedDomesticCurrency', 'SPECIFIED_CURRENCY');

    if (this.index) {
      this.fetchAndUpdateValues().subscribe();
    }
    
  }

  addRuleToCurrencyField(field: string, changeType: string): void {
    merge(
      this.obligationCharacteristicsControl.valueChanges,
      this.physicalSettlementMatrixControl.valueChanges
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe(() =>{
      this.checkIfPhysicalMatrixIsApplicable(field, changeType);
    });

    this.obligationCharacteristicsControl.updateValueAndValidity();
  }

  checkIfPhysicalMatrixIsApplicable(field: string, changeType: string): void {
    const { value: applicable } = this.physicalSettlementMatrixControl;

    if (applicable) {
      this.form.get(field)['readOnly'] = true
    } else {
      this.form.get(field)['readOnly'] = !this.obligationCharacteristicsControl.value?.includes(changeType)
    }
  }

  fetchAndUpdateValues(): Observable<Partial<PhysicalSettlementMatrix>> {
    return this.staticDataService
      .getPhysicalMatrixDataByTransactionType(this.entity.transactionType)
      .pipe(
        take(1),
        catchError((error) => throwError(error)),
        map((data: Partial<PhysicalSettlementMatrix>) => {
          if (!data) {
            return {};
          }
          
          const value = { };
          Object.keys(data)
          .filter((key: string) => typeof data[key] === 'string' || Array.isArray(data[key]))
          .forEach((key: string) => {
            if (!this.form.get(key)?.value) {
              value[key] = data[key];
            }
          });

          this.form.patchValue(value);

          return data;
      })
    );
  }

  get physicalSettlementMatrixControl(): UntypedFormControl {
    return this.form.parent.parent.get('physicalSettlementMatrix') as UntypedFormControl;
  }

  get obligationCharacteristicsControl(): UntypedFormControl {
    return this.form.get('obligationCharacteristics') as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

}
